<template>
  <div v-if="canUploadScores && entries.length" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <div class="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
          <i class="pi pi-chart-bar text-sm"></i>
        </div>
        <h3 class="font-bold text-gray-800 text-sm">Score Entry Progress</h3>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-4">
      <ProgressSpinner style="width: 24px; height: 24px" />
    </div>

    <!-- Entries -->
    <div v-else class="space-y-3">
      <div
        v-for="entry in entries.slice(0, 6)"
        :key="entry.entityId + (entry.classroomSectionId || '')"
        class="px-3 py-2.5 rounded-lg bg-gray-50"
      >
        <div class="flex items-center justify-between mb-1.5">
          <p class="text-xs font-semibold text-gray-800 truncate flex-1">
            {{ entry.name }}
            <span v-if="entry.classroomSection" class="text-gray-400 font-normal">
              ({{ entry.classroomSection }})
            </span>
          </p>
          <span class="text-[10px] font-medium ml-2 shrink-0" :class="progressColor(entry)">
            {{ entry.studentsWithScores }}/{{ entry.totalStudents }}
          </span>
        </div>
        <!-- Progress bar -->
        <div class="w-full bg-gray-200 rounded-full h-1.5">
          <div
            class="h-1.5 rounded-full transition-all"
            :class="barColor(entry)"
            :style="{ width: progressPercent(entry) + '%' }"
          />
        </div>
      </div>
      <p v-if="entries.length > 6" class="text-[10px] text-gray-400 text-center pt-1">
        +{{ entries.length - 6 }} more
      </p>
    </div>

    <!-- Action -->
    <div class="mt-4 pt-3 border-t border-gray-100">
      <router-link
        :to="{ name: 'admin-results' }"
        class="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-medium"
      >
        <i class="pi pi-upload text-[10px]"></i>
        Enter Scores
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';

import { useStaffDashboard } from '@/composables/useStaffDashboard';
import type { PendingScoreEntry } from '@/types/composition-functions';
import { GET_MY_PENDING_SCORE_ENTRIES } from '@/graphql';

const { canUploadScores } = useStaffDashboard();

const { result, loading } = useQuery(GET_MY_PENDING_SCORE_ENTRIES, null, {
  fetchPolicy: 'cache-and-network',
  enabled: canUploadScores,
});

const entries = computed<PendingScoreEntry[]>(() => result.value?.myPendingScoreEntries ?? []);

const progressPercent = (e: PendingScoreEntry) =>
  e.totalStudents > 0 ? Math.round((e.studentsWithScores / e.totalStudents) * 100) : 0;

const progressColor = (e: PendingScoreEntry) => {
  const pct = progressPercent(e);
  if (pct === 100) return 'text-primary';
  if (pct === 0) return 'text-red-500';
  return 'text-amber-600';
};

const barColor = (e: PendingScoreEntry) => {
  const pct = progressPercent(e);
  if (pct === 100) return 'bg-primary';
  if (pct === 0) return 'bg-red-400';
  return 'bg-amber-400';
};
</script>
